// Curated luxury honeymoon experiences — used by /honeymoon-experiences (hub LP)
// and /honeymoon-experiences/:slug (per-destination deep-dive).
// Copy is deliberately experience-first (private charters, chef dinners,
// helicopters, secluded villas) — not "package" language.

export const LUXURY_HONEYMOON_PHONE = "+917888370003"; // display: +91 78883 70003
export const LUXURY_HONEYMOON_PHONE_DISPLAY = "+91 78883 70003";

export const LUXURY_DESTINATIONS = [
  {
    slug: "amalfi-coast",
    name: "Amalfi Coast",
    country: "Italy",
    tagline: "Positano, Capri & Ravello — arrive by Riva speedboat.",
    heroLine: "Private Riva Speedboat Charters · Michelin Cliffside Dinners",
    hero: "https://images.pexels.com/photos/2265876/pexels-photo-2265876.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920",
    thumb: "https://images.pexels.com/photos/2265876/pexels-photo-2265876.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    signatureExperiences: [
      { title: "Private Riva Aquarama Charter", body: "Positano → Capri → Nerano, aboard a restored 1960s Riva with your own captain, champagne, and lunch at Lo Scoglio." },
      { title: "Michelin Dinner at Il Refettorio", body: "Cliffside 2-star tasting menu in Ravello, arriving by private car with a violinist for your anniversary night." },
      { title: "Blue Grotto at Dawn", body: "Enter the grotto before the day-trippers arrive — a private rowboat, two flutes of prosecco, ten uninterrupted minutes." },
      { title: "Villa Cimbrone Ceremony", body: "Renew your vows at the Terrace of Infinity, sunset over the Tyrrhenian Sea, photographer included." },
    ],
    stayIdeas: [
      { name: "Le Sirenuse, Positano", note: "Iconic pink façade, terrace suites with sea views." },
      { name: "Palazzo Avino, Ravello", note: "18th-century palace, private chef in-suite on request." },
      { name: "Grand Hotel Convento di Amalfi", note: "A 13th-century monastery converted to a 5★ hotel — cliffside pool, monastic silence." },
    ],
    idealFor: "Couples who want cinematic Italy — cliffs, lemon groves, Aperol at golden hour.",
    duration: "6–9 nights",
    bestSeason: "May, June, September, early October",
    priceFrom: "₹6.9 L / couple",
  },
  {
    slug: "switzerland",
    name: "Switzerland",
    country: "Switzerland",
    tagline: "Zermatt & Interlaken — glacier flights over the Matterhorn.",
    heroLine: "Private Helicopter Glacier Flights · Alpine Chalets with Butler",
    hero: "https://images.pexels.com/photos/236699/pexels-photo-236699.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920",
    thumb: "https://images.pexels.com/photos/236699/pexels-photo-236699.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    signatureExperiences: [
      { title: "Matterhorn Helicopter with Glacier Landing", body: "45-minute chartered flight from Zermatt with a champagne breakfast on a glacier plateau — your pilot doubles as photographer." },
      { title: "Private Glacier Express Cabin", body: "The world's slowest express train, in a fully reserved panoramic coach — sommelier-curated Swiss wine pairings the length of the route." },
      { title: "Fondue by Firelight at Iglu-Dorf", body: "A private hand-carved ice suite at 2,620m, fondue for two, sheepskin blankets, and a walk under the Milky Way." },
      { title: "Sunrise Paragliding Over Interlaken", body: "Tandem paraglide over Lake Thun at first light, then breakfast at Victoria-Jungfrau's terrace." },
    ],
    stayIdeas: [
      { name: "The Chedi Andermatt", note: "Asian-alpine crossover — the honeymoon spa suite is legendary." },
      { name: "Grand Hotel Zermatt Zermatterhof", note: "Traditional 5★ with private butler and Matterhorn-facing suites." },
      { name: "Victoria-Jungfrau Grand Hotel, Interlaken", note: "Belle Époque grande dame, the ESPA spa, lake views." },
    ],
    idealFor: "Couples chasing altitude — glaciers, snow, and cinematic mountain silences.",
    duration: "7–10 nights",
    bestSeason: "June–September (green season), December–February (winter)",
    priceFrom: "₹8.4 L / couple",
  },
  {
    slug: "hawaii",
    name: "Hawaii",
    country: "USA",
    tagline: "Maui & Kauai — volcano heli-tours and secret black-sand coves.",
    heroLine: "Doors-Off Helicopter Tours · Private Luau on Your Villa Beach",
    hero: "https://images.pexels.com/photos/753626/pexels-photo-753626.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920",
    thumb: "https://images.pexels.com/photos/753626/pexels-photo-753626.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    signatureExperiences: [
      { title: "Doors-Off Heli Over Nā Pali Coast", body: "Kauai's cathedral cliffs from 800ft with the doors removed — a private cameraman capturing every second." },
      { title: "Sunrise on Haleakalā", body: "Private 4x4 to the 3,055m summit for the Hawaiian sunrise blessing, then a picnic prepared by a native chef." },
      { title: "Private Luau on Your Villa Beach", body: "Fire dancers, an ipu-heke drummer, imu-roasted pig — hosted exclusively for the two of you on a private stretch of sand." },
      { title: "Molokini Crater Sail", body: "A crewed catamaran to the crescent-shaped marine sanctuary, snorkelling at sunrise before any other boat arrives." },
    ],
    stayIdeas: [
      { name: "Four Seasons Resort Maui at Wailea", note: "Poolside Serenity Pool suites — the only adults-only pool in Maui with butler service." },
      { name: "Montage Kapalua Bay", note: "Private residential suites overlooking the D.T. Fleming beach." },
      { name: "1 Hotel Hanalei Bay, Kauai", note: "Rainforest-facing residences with private plunge pools." },
    ],
    idealFor: "Couples who want soft-adventure with resort polish — volcano, ocean, luau, no compromises.",
    duration: "8–11 nights (add 2 nights transit)",
    bestSeason: "April, May, September, October",
    priceFrom: "₹12.5 L / couple",
  },
  {
    slug: "bali",
    name: "Bali",
    country: "Indonesia",
    tagline: "Ubud & Uluwatu — private jungle villas and cliff-edge dinners.",
    heroLine: "Cliffside Private Villas · Balinese Blessing Ceremony for Two",
    hero: "https://images.pexels.com/photos/1802255/pexels-photo-1802255.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920",
    thumb: "https://images.pexels.com/photos/1802255/pexels-photo-1802255.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    signatureExperiences: [
      { title: "Balinese Melukat Blessing", body: "A traditional water-purification ceremony at Tirta Empul, led by a family priest — a rare, intimate way to begin married life." },
      { title: "Cliffside Dinner at Rock Bar", body: "Private table on the Ayana's cliffside rock, sun sinking into the Indian Ocean, five-course tasting by a private chef." },
      { title: "Private Rice-Field Breakfast", body: "Floating breakfast served on your villa's edgeless pool overlooking the Tegallalang terraces — 6:30am, mist still rising." },
      { title: "Nusa Penida Yacht Day", body: "A crewed 42ft catamaran to Kelingking Beach, snorkelling with manta rays, sundowners on the way home." },
    ],
    stayIdeas: [
      { name: "Bulgari Resort Bali, Uluwatu", note: "Cliff-edge villa with private pool 150m above the Indian Ocean." },
      { name: "Capella Ubud", note: "Tented luxury in the Wanagiri rainforest — a modern hommage to Alfred Wallace." },
      { name: "Four Seasons Sayan", note: "Curved lotus-pond entrance, riverside villas, the world's best bathtub view." },
    ],
    idealFor: "Couples who want jungle-and-cliffs contrast — ceremony, spa, sunsets.",
    duration: "7–10 nights",
    bestSeason: "April–October",
    priceFrom: "₹4.2 L / couple",
  },
  {
    slug: "maldives",
    name: "Maldives",
    country: "Maldives",
    tagline: "Overwater residences, seaplanes, and dinners under the stars.",
    heroLine: "Overwater Residences · Private Sandbank Dinners · Seaplane Arrivals",
    hero: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920",
    thumb: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    signatureExperiences: [
      { title: "Private Sandbank Dinner", body: "A pontoon delivers you to a virgin sandbank at sunset — chef, sommelier, torch-lit table, ocean on every side." },
      { title: "Underwater Restaurant at 5.8m", body: "The world's first all-glass undersea restaurant at Hurawalhi — tasting menu with reef sharks as your neighbours." },
      { title: "Manta Ray Night Snorkel", body: "Baa Atoll's UNESCO biosphere — dive-master, plankton-attracting lights, up to 20 mantas in a single session (June–October)." },
      { title: "Seaplane Sunrise Tour", body: "Doors-open seaplane over the atolls at 6am — champagne breakfast served on a floating platform on your return." },
    ],
    stayIdeas: [
      { name: "Soneva Jani", note: "Overwater residences with a slide from the deck into the lagoon, private observatory access." },
      { name: "Cheval Blanc Randheli", note: "LVMH's Maldives flagship — sublime service, the best Guerlain spa in the region." },
      { name: "One&Only Reethi Rah", note: "Half-kilometre villas with private beaches, seven restaurants, and the region's finest wine cellar." },
    ],
    idealFor: "Couples who want to disappear — no roads, no crowds, only sand and sea.",
    duration: "5–8 nights",
    bestSeason: "November–April (dry season), May–October (secret mantas)",
    priceFrom: "₹5.8 L / couple",
  },
  {
    slug: "seychelles",
    name: "Seychelles",
    country: "Seychelles",
    tagline: "Mahé, Praslin & La Digue — granite boulders and turquoise silence.",
    heroLine: "Private Catamaran Island-Hopping · Chef-Curated Beach Barbecues",
    hero: "https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920",
    thumb: "https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    signatureExperiences: [
      { title: "Private Catamaran to St Pierre", body: "A three-island charter — Anse Lazio for lunch, Curieuse for the giant tortoises, St Pierre for the snorkel of your life." },
      { title: "Anse Source d'Argent at Dawn", body: "The world's most photographed beach — visit it before the day-trippers with a private guide and a Creole breakfast basket." },
      { title: "Vallée de Mai Rainforest Walk", body: "The 'Garden of Eden' Praslin palm forest — private botanist, endemic Coco de Mer, colour-of-a-parakeet interior." },
      { title: "Chef Barbecue on a Deserted Beach", body: "Our private-boat crew drops you on a beach with no name — a chef's fire, fresh red snapper, hammock naps between courses." },
    ],
    stayIdeas: [
      { name: "Six Senses Zil Pasyon, Félicité", note: "Private-island villas, granite-boulder pool, one of the finest spas in the Indian Ocean." },
      { name: "Constance Lémuria, Praslin", note: "The only 18-hole golf course in Seychelles — three ocean-front beaches." },
      { name: "Four Seasons Desroches Island", note: "Whole-island exclusivity — cycle to breakfast, no cars, no clocks." },
    ],
    idealFor: "Couples who want the Maldives feeling with more to explore — hikes, giants, granite.",
    duration: "7–10 nights",
    bestSeason: "April, May, October, November (shoulder — best weather + fewest guests)",
    priceFrom: "₹7.9 L / couple",
  },
];

export const LUXURY_COMPARISON = [
  { standard: "Fixed group departure dates",              private: "Departs the day you choose — any day, any duration" },
  { standard: "Shared coach & pre-set sightseeing",       private: "Private chauffeured Mercedes / Range Rover with English-speaking driver" },
  { standard: "3★ / 4★ chain hotels, standard rooms",     private: "5★ boutique villas, overwater residences, cliffside suites" },
  { standard: "Buffet meals at hotel restaurants",        private: "Private chef dinners on your terrace / a deserted beach / a glacier plateau" },
  { standard: "Guided group tours you can't customise",   private: "100% bespoke — helicopters, yachts, Michelin, Blue Grotto at dawn" },
  { standard: "Office-hours support desk (10–6)",         private: "24/7 dedicated concierge on your WhatsApp, from arrival to farewell" },
  { standard: "'Best available' economy flights",         private: "Business-class flight sourcing, priority visa, lounge access" },
  { standard: "One-size photography (nothing personal)",  private: "A private honeymoon photographer for a half-day of couple portraits" },
];
